// eslint-disable-next-line import/named
// noinspection HtmlUnknownAttribute
import { allowPageControlTabChange, isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import { buttonsSaveCancel } from '../../../webmodule-common/other/ui/modal-confirmation';
import { cache } from '../../dealer-franchisee/cache-impl/cache-registry';
import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { customElement } from 'lit/decorators.js';
import { DataEntryTabManagerView, ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { EventSnippet, EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import { getApiFactory } from '../../api/api-injector';
import { html, TemplateResult } from 'lit';
import { LangTermsConditionsView } from '../../../localization/scripts/franchisee/view/terms-conditions-view-lang';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabAction, TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { TermsAndConditionsApi } from '../../api/terms-and-conditions-api';
import { TermsConditionContainerManager, TermsConditionsContainer } from '../data/terms-conditions-container-manager';
import { TermsConditionsDetailView } from './terms-conditions-detail-view';
import { validId } from '../../../webmodule-common/other/ui/string-helper-functions';

export interface TermsConditionsViewOptions {
  termsConditions: TermsConditionsContainer;
  title: EventSnippet;
}

@customElement('wm-termsconditionsdataentryview')
export class TermsConditionsDataEntryView extends DataEntryTabManagerView {
  api: TermsAndConditionsApi = getApiFactory().termsAndConditions();
  termsConditions: TermsConditionsContainer;
  termsConditionsManager: TermsConditionContainerManager;
  protected readonly eventTitle: EventSnippet;
  protected detailView: TermsConditionsDetailView | null = null;

  constructor(options: TermsConditionsViewOptions) {
    super();
    this.eventTitle = options.title;

    this.termsConditions = options.termsConditions;
    this.termsConditionsManager = this.termsConditionsContainerManagerFactory();
  }

  /**
   * inherited;
   * @returns
   */
  public async afterConstruction() {
    await this.termsConditionsManager.needsTaC();
    this.detailView = await this.termsConditionsDetailViewFactory();

    //this will create the tab manager
    await super.afterConstruction();
    this.buildActionMenu();
  }

  protected createTabManager(): TabManager {
    // build static pages for each of the configured table settings
    const getInitialPages = (): TabPage[] => {
      const pages: TabPage[] = [];
      if (this.detailView) pages.push(this.detailView.createTabPage());
      return pages;
    };

    return new TabManager({
      initialIndex: 0,
      pages: () => getInitialPages(),
      hideSingleTab: true
    });
  }

  /**
   * inherited;
   * @returns
   */
  public internalDataChanged(): boolean {
    return this.termsConditionsManager.changed();
  }

  /**
   * inherited;
   * @returns
   */
  public isDataReadonly(): boolean {
    return this.termsConditionsManager.isReadonly();
  }

  /**
   * inherited;
   * @returns
   */
  public async prepareForSave(): Promise<void> {
    if (this.isDataReadonly()) return;

    this.detailView?.prepareForSave();
  }

  /**
   * inherited;
   * @returns
   */
  public getTitle(): Snippet {
    return this.eventTitle();
  }

  /**
   * inherited;
   * @returns
   */
  async allowPageSwitch(): Promise<boolean> {
    return await allowPageControlTabChange(this.getAutoSavePromptOptions());
  }

  public async saveAndClose(): Promise<boolean> {
    if (this.isDataReadonly()) return false;

    return await this.tryClose();
  }

  /**
   * inherited;
   * @returns
   */
  protected bodyTemplate(): EventTemplate {
    this.buildActionMenu();
    return super.bodyTemplate();
  }

  /**
   * inherited;
   * @returns
   */
  protected async internalSaveData(): Promise<boolean> {
    return await this.termsConditionsManager.save(isAutoSaving());
  }

  protected isNew(): boolean {
    return !validId(this.termsConditionsManager.container.termsConditionsId);
  }

  /**
   * inherited;
   * @returns
   */
  protected getValidationErrors() {
    return this.detailView?.getValidationErrors() ?? [];
  }

  protected async termsConditionsDetailViewFactory() {
    return await constructAsync(
      new TermsConditionsDetailView(this, {
        termsConditionsManger: this.termsConditionsManager
      })
    );
  }

  protected termsConditionsContainerManagerFactory(): TermsConditionContainerManager {
    const manager = new TermsConditionContainerManager(this.termsConditions, this.eventTitle, this.api);
    manager.afterSave.push(() => {
      cache().termsAndConditions.flush([this.termsConditions.termsConditionsId]);
    });
    return manager;
  }

  private buildActionMenu() {
    if (!this._tabManager) return;
    const actions: TabAction[] = [];

    const save: TabAction = {
      onClick: async () => {
        await this.saveAndClose();
      },
      caption: () => LangTermsConditionsView.saveTermsConditions()
    };

    if (!isAutoSaving()) actions.push(save);

    this.tabManager.setActions(actions);
  }
}

@customElement('wm-termsconditionsview')
export class TermsConditionsView extends ModalViewBase {
  view: TermsConditionsDataEntryView | null = null;
  options: TermsConditionsViewOptions;

  constructor(options: TermsConditionsViewOptions) {
    super();
    this.options = options;
  }

  /**
   * inherited
   * @returns
   */
  protected get modalSize() {
    return 'modal-lg';
  }

  /**
   * inherited
   * @returns
   */
  async canClose(): Promise<boolean> {
    return (await this.view?.canClose()) ?? true;
  }

  async afterConstruction(): Promise<void> {
    this.view = await constructAsync(this.createView());
  }

  /**
   * inherited
   * @returns
   */
  protected getTitle(): Snippet {
    return this.view?.getTitle() ?? '';
  }

  /**
   * inherited
   * @returns
   */
  protected footerTemplate(): TemplateResult | null {
    const saveEvent = async () => {
      if (await this.save()) await this.hideModal();
    };
    const buttons = buttonsSaveCancel();
    return this.createConfirmCancelButtons(buttons.ok, saveEvent, buttons.cancel);
  }

  protected createView(): TermsConditionsDataEntryView {
    const x = new TermsConditionsDataEntryView(this.options);
    x.owner = this;
    return x;
  }

  protected bodyTemplate(): EventTemplate {
    return html`${this.view?.ui}`;
  }

  protected renderCloseButtonTemplate(): boolean {
    return false;
  }

  protected async save() {
    return this.view?.saveAndClose();
  }
}
